// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — metadata container, evaluation graph, validation
// ═══════════════════════════════════════════════════════════════════════════
//
// This module ties together:
//   - the parameter union (dynamicBlockParameters.ts)
//   - the action union (dynamicBlockActions.ts)
//   - lookup tables and the block properties table (dynamicBlockLookup.ts)
//   - the parameter → action dependency graph (for topological evaluation)
//   - the per-INSERT state overlay (applyInstanceParameters)
//   - semantic validation (validateDynamicMetadata)
//
// `DynamicBlockMetadata` is the payload that hangs off BlockDefinition via
// the optional `dynamicMetadata` field. Static blocks leave it undefined;
// dynamic blocks populate parameters / actions / lookupTables /
// blockPropertiesTable / evaluationGraph.

import { z } from "zod";

import { dynamicActionSchema, type DynamicAction } from "./dynamicBlockActions";
import {
  dynamicParameterSchema,
  findVisibilityStateById,
  type DynamicParameter,
  type VisibilityParameter,
} from "./dynamicBlockParameters";
import {
  blockPropertiesTableSchema,
  lookupTableSchema,
} from "./dynamicBlockLookup";
import {
  geometricConstraintSchema,
  validateGeometricConstraint,
  type GeometricConstraint,
} from "./dynamicBlockConstraints";
import { dynamicGripSchema } from "./dynamicBlockGrips";

// ═══════════════════════════════════════════════════════════════════════════
// Evaluation graph — which actions depend on which parameters
// ═══════════════════════════════════════════════════════════════════════════

export const dependencyEdgeSchema = z.object({
  parameterId: z.string().min(1),
  actionId: z.string().min(1),
});
export type DependencyEdge = z.infer<typeof dependencyEdgeSchema>;

export const evaluationGraphSchema = z.object({
  edges: z.array(dependencyEdgeSchema).default([]),
  // Chain groups — DYN-3. Each inner array is an ordered list of action
  // ids that must be evaluated sequentially (output of one feeds input of
  // the next). Empty for Tier 1 / Tier 2.
  chainGroups: z.array(z.array(z.string().min(1))).default([]),
});
export type EvaluationGraph = z.infer<typeof evaluationGraphSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Dynamic block metadata container
// ═══════════════════════════════════════════════════════════════════════════

export const dynamicBlockMetadataSchema = z.object({
  // Version marker for future migrations. Tier 2 stays at version 1 because
  // the extensions are additive (new parameter / action kinds, new optional
  // fields on metadata). Bumped only when a backwards-incompatible shape
  // change lands.
  version: z.number().int().default(1),
  parameters: z.array(dynamicParameterSchema).default([]),
  actions: z.array(dynamicActionSchema).default([]),
  evaluationGraph: evaluationGraphSchema.default(() =>
    evaluationGraphSchema.parse({}),
  ),
  /**
   * Lookup tables belonging to this dynamic block. A LookupParameter's
   * `lookupTableId` points to an entry here.
   */
  lookupTables: z.array(lookupTableSchema).default([]),
  /**
   * The block properties table (one per block). Optional — blocks without
   * custom properties omit this entirely.
   */
  blockPropertiesTable: blockPropertiesTableSchema.optional(),
  /**
   * Geometric constraints (DYN-3 Tier 3). Unlike dimensional constraint
   * PARAMETERS (stored in `parameters[]` with kind=constraint-*), geometric
   * constraints are binary relations (coincident / parallel / tangent / …)
   * between entities inside the block. They do NOT appear in the Properties
   * Palette and are NOT driven by actions — they are consumed by the
   * Gauss-Newton solver (DYN-3 B) to restore invariants after every action
   * pass. Stored here so the order of authoring is preserved across DXF
   * round-trips (AutoCAD evaluates in authored order).
   */
  geometricConstraints: z.array(geometricConstraintSchema).default([]),
  /**
   * DYN-4 Fix 7 — grip node typing. AutoCAD 2018+ emits dynamic-block
   * grips (BLOCKLINEARGRIP / BLOCKVISIBILITYGRIP / BLOCKPROPERTIESTABLEGRIP
   * / …) as separate OBJECTS records with their own handle, owner
   * reference, backing-parameter handle, and optional position in block
   * space. Phase 4b stores the parsed grips here so subsequent phases can
   * render them on the canvas without re-parsing the raw OBJECTS list.
   *
   * Each grip retains its full raw-group list under `rawGroups` so the
   * writer can round-trip unchanged. Grips that defer their coordinates
   * to a paired BLOCKGRIPLOCATIONCOMPONENT expression (which lives in
   * `futureExtensions.rawObjects`) omit the `position` field.
   */
  grips: z.array(dynamicGripSchema).default([]),
  /**
   * Forward-compat escape hatch — future-tier payload that the current
   * parser doesn't understand but must round-trip unchanged. Used by DXF
   * parsers that encounter unknown ACDBBLOCK* objects.
   */
  futureExtensions: z.record(z.string(), z.unknown()).default({}),
});
export type DynamicBlockMetadata = z.infer<typeof dynamicBlockMetadataSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Action / parameter kind compatibility table
// ═══════════════════════════════════════════════════════════════════════════

// Linear-like = parameters whose value is a single 1D numeric driver
// suitable for Move / Stretch / Scale / Array. Tier 2 numeric kinds
// (polar/xy) plus Tier 2 constraint kinds (horizontal/vertical) and Tier 3
// constraint kinds (aligned/radial/diameter) qualify.
const LINEAR_LIKE = new Set<DynamicParameter["kind"]>([
  "linear",
  "polar",
  "xy",
  "constraint-horizontal",
  "constraint-vertical",
  "constraint-aligned",
  "constraint-radial",
  "constraint-diameter",
]);

// Polar-stretch-compatible = only polar parameters (Tier 3). AutoCAD's
// Polar Stretch Action is explicitly locked to Polar parameters — the
// rotation component would be meaningless on Linear / XY / constraint.
const POLAR_STRETCH_COMPATIBLE = new Set<DynamicParameter["kind"]>(["polar"]);

// Angular-like = parameters whose value is an angle in degrees suitable
// for Rotate. Tier 2 rotation + Tier 2 constraint-angular qualify.
const ANGULAR_LIKE = new Set<DynamicParameter["kind"]>([
  "rotation",
  "constraint-angular",
]);

/**
 * Return the list of parameter kinds that a given action kind accepts.
 * Used by validateDynamicMetadata to flag schema-level mismatches that the
 * Zod discriminated union alone cannot catch.
 *
 * Compatibility rules (AutoCAD parity):
 *   - move, stretch, scale, array → linear / polar / xy / constraint-horizontal / constraint-vertical / constraint-aligned / constraint-radial / constraint-diameter
 *   - rotate → rotation / constraint-angular
 *   - flip   → flip
 *   - lookup → lookup
 *   - polar-stretch → polar (Tier 3; strictly polar)
 *
 * Stretch is technically restricted to 1D drivers in AutoCAD (linear and
 * the 1D constraint kinds); polar/xy still accept it because their
 * delta computation falls back to the parameter's effective axis.
 */
export function allowedParameterKindsForAction(
  actionKind: DynamicAction["kind"],
): ReadonlySet<DynamicParameter["kind"]> {
  switch (actionKind) {
    case "move":
    case "stretch":
    case "scale":
    case "array":
      return LINEAR_LIKE;
    case "rotate":
      return ANGULAR_LIKE;
    case "flip":
      return new Set(["flip"]);
    case "lookup":
      return new Set(["lookup"]);
    case "polar-stretch":
      return POLAR_STRETCH_COMPATIBLE;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Validation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Run semantic validation over a DynamicBlockMetadata instance beyond what
 * Zod enforces structurally. Returns an array of error messages; an empty
 * array means the metadata is self-consistent.
 *
 * Checks (Tier 1 preserved + Tier 2 added + Tier 3 added):
 *   * parameter names are unique within the block
 *   * parameter ids are unique within the block
 *   * every action's parameterId points to an existing parameter
 *   * action ids are unique within the block
 *   * action kind matches the driving parameter's kind (Tier 2 + Tier 3)
 *   * every Visibility parameter's defaultStateId / currentStateId references
 *     one of its own states
 *   * every Visibility parameter state has a unique id within that parameter
 *   * Lookup parameters reference an existing lookupTables[].id (Tier 2)
 *   * lookupTables input columns reference existing parameters (Tier 2)
 *   * lookup table ids are unique (Tier 2)
 *   * evaluation-graph edges reference parameters and actions that exist
 *   * chain-group entries reference actions that exist
 *   * Tier 3 geometric constraints pass per-kind arity / referenced-id
 *     validation (see validateGeometricConstraint in dynamicBlockConstraints)
 *   * `knownEntityIds` (optional) — when passed, geometric constraint
 *     entityIds and Tier 3 action selection/frame references are checked
 *     against the block's entity set
 */
export function validateDynamicMetadata(
  meta: DynamicBlockMetadata,
  knownEntityIds?: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];

  // ── Parameters ──
  const paramIds = new Set<string>();
  const paramNames = new Set<string>();
  const paramById = new Map<string, DynamicParameter>();
  for (const p of meta.parameters) {
    if (paramIds.has(p.id)) errors.push(`Duplicate parameter id: ${p.id}`);
    paramIds.add(p.id);
    paramById.set(p.id, p);
    if (paramNames.has(p.name))
      errors.push(`Duplicate parameter name: ${p.name}`);
    paramNames.add(p.name);

    if (p.kind === "visibility") {
      const stateIds = new Set<string>();
      for (const s of p.states) {
        if (stateIds.has(s.id)) {
          errors.push(
            `Visibility parameter ${p.name} has duplicate state id: ${s.id}`,
          );
        }
        stateIds.add(s.id);
      }
      if (!stateIds.has(p.defaultStateId)) {
        errors.push(
          `Visibility parameter ${p.name} defaultStateId invalid: ${p.defaultStateId}`,
        );
      }
      if (!stateIds.has(p.currentStateId)) {
        errors.push(
          `Visibility parameter ${p.name} currentStateId invalid: ${p.currentStateId}`,
        );
      }
    }
  }

  // ── Lookup tables ──
  // Defensive: `lookupTables` was added in DYN-2; older callers that build
  // a DynamicBlockMetadata via object-literal (DXF parser, test fixtures)
  // may still omit the field. Fall back to empty array rather than throwing
  // so the Tier 1 call sites keep working until they are migrated.
  const tableIds = new Set<string>();
  for (const tbl of meta.lookupTables ?? []) {
    if (tableIds.has(tbl.id)) {
      errors.push(`Duplicate lookup table id: ${tbl.id}`);
    }
    tableIds.add(tbl.id);
    for (const col of tbl.inputColumns) {
      if (!paramIds.has(col.parameterId)) {
        errors.push(
          `Lookup table ${tbl.id} input column references unknown parameter ${col.parameterId}`,
        );
      }
    }
  }

  // ── Lookup parameters must point at an existing table ──
  for (const p of meta.parameters) {
    if (p.kind === "lookup" && !tableIds.has(p.lookupTableId)) {
      errors.push(
        `Lookup parameter ${p.name} references unknown table ${p.lookupTableId}`,
      );
    }
  }

  // ── Actions ──
  const actionIds = new Set<string>();
  for (const a of meta.actions) {
    if (actionIds.has(a.id)) errors.push(`Duplicate action id: ${a.id}`);
    actionIds.add(a.id);
    const drivingParam = paramById.get(a.parameterId);
    if (!drivingParam) {
      errors.push(
        `Action ${a.id} references unknown parameter ${a.parameterId}`,
      );
      continue;
    }
    // Tier 2: enforce action/parameter kind match.
    const allowed = allowedParameterKindsForAction(a.kind);
    if (!allowed.has(drivingParam.kind)) {
      errors.push(
        `Action ${a.id} kind=${a.kind} requires parameter kind ∈ {${[...allowed].join(", ")}}, got ${drivingParam.kind}`,
      );
    }
  }

  // ── Evaluation graph edges ──
  for (const edge of meta.evaluationGraph.edges) {
    if (!paramIds.has(edge.parameterId)) {
      errors.push(
        `Evaluation edge references unknown parameter ${edge.parameterId}`,
      );
    }
    if (!actionIds.has(edge.actionId)) {
      errors.push(`Evaluation edge references unknown action ${edge.actionId}`);
    }
  }

  // ── Chain groups ──
  for (let g = 0; g < meta.evaluationGraph.chainGroups.length; g += 1) {
    const group = meta.evaluationGraph.chainGroups[g] ?? [];
    for (const actionId of group) {
      if (!actionIds.has(actionId)) {
        errors.push(`Chain group ${g} references unknown action ${actionId}`);
      }
    }
  }

  // ── Tier 3 geometric constraints ──
  // Defensive: `geometricConstraints` was added in DYN-3; older callers
  // that build metadata via object-literal may omit it. Fall back to
  // empty array rather than throwing.
  const geomConstraints: readonly GeometricConstraint[] =
    meta.geometricConstraints ?? [];
  if (geomConstraints.length > 0) {
    // When `knownEntityIds` is not supplied, we can't verify entity
    // references — skip that check but still enforce arity / id uniqueness.
    const entitySet: ReadonlySet<string> =
      knownEntityIds ??
      // Empty set: every entity id check will fail. Callers should pass
      // knownEntityIds when the block's entities are available; without
      // them we silently skip the per-entity check rather than produce
      // spurious errors on legitimate metadata.
      new Set<string>();
    const geomIds = new Set<string>();
    for (const c of geomConstraints) {
      if (geomIds.has(c.id)) {
        errors.push(`Duplicate geometric constraint id: ${c.id}`);
      }
      geomIds.add(c.id);
      // Skip entity-reference validation when knownEntityIds not passed.
      if (knownEntityIds !== undefined) {
        for (const msg of validateGeometricConstraint(c, entitySet)) {
          errors.push(msg);
        }
      }
    }
  }

  return errors;
}

// ═══════════════════════════════════════════════════════════════════════════
// Graph derivation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Derive the default evaluation edges from an action list. Every action
 * contributes a single edge (parameter → action).
 */
export function deriveEvaluationEdges(
  actions: readonly DynamicAction[],
): DependencyEdge[] {
  return actions.map((a) => ({ parameterId: a.parameterId, actionId: a.id }));
}

// ═══════════════════════════════════════════════════════════════════════════
// Lookup helpers (metadata-scoped)
// ═══════════════════════════════════════════════════════════════════════════

export function findParameterById(
  meta: DynamicBlockMetadata,
  id: string,
): DynamicParameter | undefined {
  return meta.parameters.find((p) => p.id === id);
}

export function findParameterByName(
  meta: DynamicBlockMetadata,
  name: string,
): DynamicParameter | undefined {
  return meta.parameters.find((p) => p.name === name);
}

export function findActionById(
  meta: DynamicBlockMetadata,
  id: string,
): DynamicAction | undefined {
  return meta.actions.find((a) => a.id === id);
}

// ═══════════════════════════════════════════════════════════════════════════
// Visibility computation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Return the set of entity ids that remain visible after applying every
 * Visibility Parameter's current state. The union is taken across all
 * Visibility parameters (AutoCAD allows multiple Visibility parameters per
 * block).
 */
export function getVisibleEntityIds(
  meta: DynamicBlockMetadata,
  allEntityIds: readonly string[],
): Set<string> {
  const hidden = new Set<string>();
  for (const p of meta.parameters) {
    if (p.kind !== "visibility") continue;
    const current = findVisibilityStateById(
      p as VisibilityParameter,
      p.currentStateId,
    );
    if (!current) continue;
    for (const id of current.invisibleEntityIds) {
      hidden.add(id);
    }
  }
  return new Set(allEntityIds.filter((id) => !hidden.has(id)));
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-INSERT overrides — merge customProperties / visibilityState onto meta
// ═══════════════════════════════════════════════════════════════════════════
//
// Customproperties keys we recognise (keyed by parameter.name by default; a
// few parameter kinds also consume explicit `<name>.x` / `<name>.y` keys):
//
//   linear                 → <name>                            (number or string-number)
//   flip                   → <name>                            (boolean / "0"|"1"/"true"|"false"/0|1)
//   visibility             → visibilityState OR <name>         (state id or name)
//   basepoint              → <none — position fixed at author time>
//   point                  → <name>.x / <name>.y               (numbers)
//   polar                  → <name>.distance / <name>.angle    (numbers, angle in deg)
//   xy                     → <name>.x / <name>.y               (numbers)
//   rotation               → <name>                            (number, degrees)
//   lookup                 → <name>                            (row id)
//   constraint-horizontal  → <name> | <name>.distance          (number)
//   constraint-vertical    → <name> | <name>.distance          (number)
//   constraint-angular     → <name> | <name>.angle             (number, degrees)
//   alignment              → <name> | <name>.angle             (number, degrees) — Tier 3
//   constraint-aligned     → <name> | <name>.distance          (number) — Tier 3
//   constraint-radial      → <name> | <name>.radius            (number) — Tier 3
//   constraint-diameter    → <name> | <name>.diameter          (number) — Tier 3

function coerceNumber(raw: unknown): number | undefined {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function coerceBoolean(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "number") return raw !== 0;
  if (typeof raw === "string") {
    if (raw === "1" || raw.toLowerCase() === "true") return true;
    if (raw === "0" || raw.toLowerCase() === "false") return false;
  }
  return undefined;
}

/**
 * Produce a new metadata instance with parameter current-values overridden
 * by the INSERT's per-instance customProperties map (keyed by parameter
 * name, with `.x` / `.y` / `.distance` / `.angle` suffixes for 2D kinds)
 * and the INSERT's visibilityState. Original metadata is NOT mutated.
 */
export function applyInstanceParameters(
  meta: DynamicBlockMetadata,
  instanceCustomProperties: Record<string, string | number | boolean>,
  instanceVisibilityState?: string,
): DynamicBlockMetadata {
  const props = instanceCustomProperties;
  const nextParameters: DynamicParameter[] = meta.parameters.map((p) => {
    switch (p.kind) {
      case "linear": {
        const n = coerceNumber(props[p.name]);
        return n !== undefined ? { ...p, currentDistance: n } : p;
      }
      case "flip": {
        const b = coerceBoolean(props[p.name]);
        return b !== undefined ? { ...p, currentValue: b } : p;
      }
      case "visibility": {
        // Dedicated visibilityState field wins over customProperties.
        if (
          typeof instanceVisibilityState === "string" &&
          instanceVisibilityState.length > 0
        ) {
          const matched =
            p.states.find((s) => s.id === instanceVisibilityState) ??
            p.states.find((s) => s.name === instanceVisibilityState);
          if (matched) return { ...p, currentStateId: matched.id };
        }
        const raw = props[p.name];
        if (typeof raw === "string" && raw.length > 0) {
          const matched =
            p.states.find((s) => s.id === raw) ??
            p.states.find((s) => s.name === raw);
          if (matched) return { ...p, currentStateId: matched.id };
        }
        return p;
      }
      case "basepoint":
        return p;
      case "point": {
        const x = coerceNumber(props[`${p.name}.x`]);
        const y = coerceNumber(props[`${p.name}.y`]);
        if (x === undefined && y === undefined) return p;
        return {
          ...p,
          currentPoint: {
            x: x ?? p.currentPoint.x,
            y: y ?? p.currentPoint.y,
          },
        };
      }
      case "polar": {
        const dist = coerceNumber(props[`${p.name}.distance`]);
        const ang = coerceNumber(props[`${p.name}.angle`]);
        if (dist === undefined && ang === undefined) return p;
        return {
          ...p,
          currentDistance: dist ?? p.currentDistance,
          currentAngle: ang ?? p.currentAngle,
        };
      }
      case "xy": {
        const x = coerceNumber(props[`${p.name}.x`]);
        const y = coerceNumber(props[`${p.name}.y`]);
        if (x === undefined && y === undefined) return p;
        return {
          ...p,
          currentX: x ?? p.currentX,
          currentY: y ?? p.currentY,
        };
      }
      case "rotation": {
        const ang = coerceNumber(props[p.name]);
        return ang !== undefined ? { ...p, currentAngle: ang } : p;
      }
      case "lookup": {
        const raw = props[p.name];
        if (typeof raw === "string" && raw.length > 0) {
          return { ...p, currentLookupRowId: raw };
        }
        return p;
      }
      case "constraint-horizontal":
      case "constraint-vertical": {
        // DYN-2 Batch C: distance-based constraint parameters expose a
        // single numeric current value via `<name>` (matches AutoCAD's
        // Properties Palette key). The optional `<name>.distance`
        // suffix is also accepted for symmetry with Polar.
        const n =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.distance`]);
        return n !== undefined ? { ...p, currentDistance: n } : p;
      }
      case "constraint-angular": {
        // Angular constraint exposes an angle (degrees). Accepts
        // `<name>` directly or `<name>.angle` for symmetry with Polar.
        const ang =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.angle`]);
        return ang !== undefined ? { ...p, currentAngle: ang } : p;
      }
      case "alignment": {
        // DYN-3 Tier 3: Alignment exposes a runtime-rotation angle
        // (degrees) via `<name>` or `<name>.angle`. Auto-rotation on
        // INSERT writes directly to customProperties[`${name}.angle`] —
        // evaluator consumes it uniformly regardless of who set it.
        const ang =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.angle`]);
        return ang !== undefined ? { ...p, currentAngle: ang } : p;
      }
      case "constraint-aligned": {
        // DYN-3 Tier 3: Aligned constraint exposes a distance along the
        // authored axis. Same coercion pattern as horizontal / vertical.
        const n =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.distance`]);
        return n !== undefined ? { ...p, currentDistance: n } : p;
      }
      case "constraint-radial": {
        // DYN-3 Tier 3: Radial constraint exposes a radius. Accepts
        // `<name>` or `<name>.radius`.
        const r =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.radius`]);
        return r !== undefined ? { ...p, currentRadius: r } : p;
      }
      case "constraint-diameter": {
        // DYN-3 Tier 3: Diameter constraint exposes a diameter. Accepts
        // `<name>` or `<name>.diameter`.
        const d =
          coerceNumber(props[p.name]) ??
          coerceNumber(props[`${p.name}.diameter`]);
        return d !== undefined ? { ...p, currentDiameter: d } : p;
      }
      case "user": {
        // DYN-4 Fix 3: User parameter — named scalar backed by
        // ACDBASSOCVARIABLE. The instance-level override lives under
        // `<name>` (primary) or `<name>.value` (symmetric with Polar's
        // compound keys). Numeric for scalar variable types, string for
        // `string`-typed variables.
        const raw = props[p.name] ?? props[`${p.name}.value`];
        if (raw === undefined) return p;
        if (p.valueType === "string") {
          const s = typeof raw === "string" ? raw : String(raw);
          return { ...p, defaultValue: s };
        }
        const n = coerceNumber(raw);
        return n !== undefined ? { ...p, defaultValue: n } : p;
      }
      default: {
        // Exhaustiveness guard — if a new kind is added, TypeScript flags here.
        const _never: never = p;
        return _never;
      }
    }
  });

  return {
    ...meta,
    parameters: nextParameters,
  };
}
